import { useGesture } from '@use-gesture/react';
import { clsx } from 'clsx';
import {
  type ComponentPropsWithoutRef,
  forwardRef,
  memo,
  type PropsWithChildren,
  useRef,
  useState,
} from 'react';

import * as styles from './Overlay.sty.js';
import { useFocusRing } from '../../../core/hooks/useFocusRing.js';
import { clamp } from '../../split-layout/width-utils.js';

export const OVERLAY_HANDLE_WIDTH = 8;

/** @internal */
export interface OverlayProps
  extends PropsWithChildren,
    ComponentPropsWithoutRef<'div'> {
  resizable?: boolean;
  onResize?: (width: number) => void;
  onResizeEnd?: (width: number) => void;
  minWidth?: number;
  maxWidth?: number;
  /**
   * The controlled content width in px. The overlay renders this width except
   * while the user is actively dragging the resize handle.
   */
  width?: number;
}

/** @internal */
export const Overlay = memo(
  forwardRef<HTMLDivElement, OverlayProps>((props, forwardedRef) => {
    const {
      children,
      className,
      style,
      minWidth = 200,
      maxWidth = Infinity,
      width = 300,
      resizable = true,
      onResize,
      onResizeEnd,
      ...restProps
    } = props;

    const containerRef = useRef<HTMLDivElement>(null);
    const handleRef = useRef<HTMLHRElement>(null);

    const { focusProps, focusClassName } = useFocusRing({
      focusWithin: true,
      ignoreModality: true,
    });

    const dragStartWidth = useRef<number | null>(null);
    const lastWidthRef = useRef<number | null>(null);
    const clampContentWidth = (value: number) =>
      clamp(minWidth, value, maxWidth);

    // While the user is actively dragging, the overlay owns its width locally
    // for a smooth interaction; otherwise the width is fully controlled by the
    // `width` prop. Committing the drag clears the local value so the
    // controlled width takes over again.
    const [dragWidth, setDragWidth] = useState<number | null>(null);

    const contentWidth = dragWidth ?? clampContentWidth(width);

    const overlayWidth = resizable
      ? contentWidth + OVERLAY_HANDLE_WIDTH
      : contentWidth;

    useGesture(
      {
        onDragStart: () => {
          dragStartWidth.current = clampContentWidth(contentWidth);
        },
        onDragEnd: () => {
          const finalWidth =
            lastWidthRef.current ?? clampContentWidth(contentWidth);
          dragStartWidth.current = null;
          lastWidthRef.current = null;
          setDragWidth(null);
          onResizeEnd?.(finalWidth);
        },
        onDrag: (state) => {
          if (dragStartWidth.current === null) {
            return;
          }

          const nextWidth = clamp(
            minWidth,
            dragStartWidth.current + state.movement[0] * -1,
            maxWidth,
          );
          lastWidthRef.current = nextWidth;
          setDragWidth(nextWidth);
          onResize?.(nextWidth);
        },
      },
      { target: handleRef },
    );

    return (
      <div
        role="dialog"
        className={styles.overlay}
        style={{ ...style, width: overlayWidth }}
        ref={containerRef}
      >
        {resizable ? (
          <div className={styles.handleContainer}>
            <hr
              {...focusProps}
              ref={handleRef}
              className={clsx(styles.resizeHandle, focusClassName)}
              tabIndex={0}
            />
          </div>
        ) : null}
        <div
          {...restProps}
          ref={forwardedRef}
          className={clsx(styles.overlayContent, className)}
        >
          {children}
        </div>
      </div>
    );
  }),
);
Overlay.displayName = 'Overlay';
